import { Clock, FileText, Home, User } from "lucide-react";

import type { RouteTable } from "@hakili/app-shell";

/**
 * `P0-UIK-016`: the client app's routes, and its three tabs.
 *
 * 4.6.4 gives the paths literally , `/`, `/recherche`, `/vehicule/:id`,
 * `/eligibilite`, `/dossier` and its four sub-screens, `/profil`, plus the
 * public `/depot/:token` , and 4.6.1 gives the shell: *"Single column,
 * `max-w-md` centred, bottom tab bar (`Rechercher`, `Mon dossier`, `Profil`),
 * one screen at a time."*
 *
 * **Danger sentence: the client has no whitelist, and that is not an
 * oversight.** The console and the bank derive a screen set from a role. A
 * buyer has one role and every client screen belongs to him, so the guard here
 * is not about WHICH screens exist , it is about the public route. 6.3.6 makes
 * `/depot/:token` reachable with no session at all, because the token IS the
 * authorisation and the person holding it is a client photographing a document
 * from a link sent by SMS. Sending them to a login they cannot pass is the dead
 * end 4.6.6 forbids.
 *
 * So `TOUS_LES_ECRANS` is the whitelist, and it is written as a derived
 * constant rather than as a literal set: a screen added to the table without
 * being added to the whitelist would be a 404 for a screen the app itself
 * renders, which is the kind of defect that survives review.
 */

export const ECRANS_CLIENT = [
  "accueil",
  "recherche",
  "vehicule",
  // `AVAILABILITY-QUEUE`, `M2`, point 3: « Véhicules disponibles pour vous ».
  "queueFront",
  "eligibilite",
  "dossier",
  "suivi",
  // `round C3-A`, `C3-A.2`: `WF-CLI-10`'s own screen. It was the one stage
  // where the BUYER owes the next gesture and no control reached it.
  "paiement",
  "pieces",
  "offres",
  "assurance",
  "retrait",
  // `T-2b`: the cash purchase's conclusion (WF-CLI-28).
  "comptant",
  "profil",
  // `REG-PROFILE-001`, Arc B: the declared profile editor, reached from « Mon compte ».
  "declaration",
  "depot",
] as const;

export type EcranClient = (typeof ECRANS_CLIENT)[number];

export const ROUTES: RouteTable = {
  landingScreen: "accueil",
  routes: [
    { path: "/", screen: "accueil" },
    { path: "/recherche", screen: "recherche" },
    { path: "/vehicule/:id", screen: "vehicule" },
    { path: "/queue-front", screen: "queueFront" },
    { path: "/eligibilite", screen: "eligibilite" },
    { path: "/dossier", screen: "dossier" },
    { path: "/suivi", screen: "suivi" },
    { path: "/paiement", screen: "paiement" },
    { path: "/dossier/pieces", screen: "pieces" },
    { path: "/dossier/offres", screen: "offres" },
    { path: "/dossier/assurance", screen: "assurance" },
    { path: "/dossier/retrait", screen: "retrait" },
    { path: "/dossier/comptant", screen: "comptant" },
    { path: "/profil", screen: "profil" },
    { path: "/profil/declaration", screen: "declaration" },
    // 6.3.6: the token is the authorisation. No session, no tab bar, no login.
    { path: "/depot/:token", screen: "depot", public: true },
  ],
};

/** Every screen the table serves. Derived, so the two cannot disagree. */
export const TOUS_LES_ECRANS: ReadonlySet<string> = new Set(ROUTES.routes.map((route) => route.screen));

/**
 * The tabs, **four and not three**, in the DRAWING's order.
 *
 * `round CW`, `CW.1`, flagged rather than silent: 04 §4.6.1 names three, and
 * `hakili-client.jsx`'s `onglets` names four , Rechercher, Mon dossier, Suivi,
 * Profil. The drawing is later and more specific and wins; both texts are kept,
 * and 04's row is amended by record in `CW.8` on 09's form for a canon
 * amendment, never silently.
 *
 * **Why the split is real and not decoration**: « Mon dossier » is the buyer's
 * PIECES , what he owes and what he has deposited , and « Suivi » is where his
 * demand stands. They answer two different questions, and the drawing gives the
 * second its own tab because it is the one a buyer opens every day while he
 * waits.
 *
 * **A tab is lit by a SET of screens, not by one path.** A buyer on
 * `/dossier/pieces` is still inside "Mon dossier", and a tab bar that went dark
 * the moment he opened his documents would tell him he had left the section he
 * is plainly in. That reads as a bug in the only place the product has to feel
 * solid.
 */
export const ONGLETS = [
  {
    key: "client.nav.recherche",
    path: "/recherche",
    icon: Home,
    /**
     * `round C2`, `C2.9`: **« Éligibilité » lights RECHERCHER**, and the pair
     * is what moved it.
     *
     * `CW` put it under « Mon dossier », on the reasoning that a buyer filing
     * his figures is inside his file. The drawing of 9 September lights
     * « Rechercher » on that screen, and it is right for a reason the reasoning
     * missed: a buyer reaches it from a CAR's fiche, before any file exists, and
     * a lit « Mon dossier » tells him he has one. Both texts kept; the drawing
     * is later and more specific and wins.
     */
    ecrans: ["accueil", "recherche", "vehicule", "queueFront", "eligibilite"],
  },
  {
    key: "client.nav.dossier",
    path: "/dossier",
    icon: FileText,
    ecrans: ["dossier", "pieces"],
  },
  {
    key: "client.nav.suivi",
    path: "/suivi",
    icon: Clock,
    /**
     * `round C3-A`, `C3-A.2`: **`paiement` lights SUIVI**, because that is
     * where the buyer comes from and goes back to. `WF-CLI-10`'s trigger is
     * « Régler les frais » on the tracking card and its §9 returns to « Suivi »,
     * so a tab bar that went dark on the payment screen would tell him he had
     * left the section he is plainly in.
     */
    ecrans: ["suivi", "paiement", "offres", "assurance", "retrait", "comptant"],
  },
  { key: "client.nav.profil", path: "/profil", icon: User, ecrans: ["profil", "declaration"] },
] as const satisfies readonly {
  key: string;
  path: string;
  /** The drawing's own glyph , `OD-19` closed on `lucide-react`, not on hand-drawn paths. */
  icon: typeof Home;
  ecrans: readonly EcranClient[];
}[];

/** The tab that should look active for a screen, or `null` on a public route. */
export function ongletActif(ecran: string): (typeof ONGLETS)[number] | null {
  return ONGLETS.find((onglet) => (onglet.ecrans as readonly string[]).includes(ecran)) ?? null;
}
